import React, { useContext, useMemo, useState } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ChevronDown, ChevronUp, Eye, EyeSlash, PlugFill } from 'react-bootstrap-icons';
import PrimaryButton from '../shared/PrimaryButton';
import ConnectBrokerModal from '../broker/ConnectBrokerModal';
import { useTheme } from '../../contexts/ThemeContext';
import { AuthContext } from '../../contexts/AuthContext';
import { isBrokerLinked, formatBrokerDisplayName } from '../../utils/brokerConnection';
import { getAccountDisplayLabel } from '../../utils/accountNickname';
import './AccountValueCard.css';

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fmtCurrency(num) {
  if (num == null) return null;
  const n = typeof num === 'number' ? num : parseFloat(num);
  if (!Number.isFinite(n)) return null;
  return currencyFmt.format(n);
}

function fmtPercent(value) {
  if (value == null || !Number.isFinite(value)) return null;
  return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
}

function plAccent(num, colors, { zeroIsWhite = false } = {}) {
  if (num == null || !Number.isFinite(num)) return colors.textMuted;
  const epsilon = 0.005;
  if (zeroIsWhite && Math.abs(num) <= epsilon) return colors.white || colors.text;
  if (num > 0) return colors.success || colors.chartUp;
  if (num < 0) return colors.danger || colors.chartDown;
  return colors.text;
}

function MetricSkeleton({ wide = false }) {
  return (
    <span
      className={[
        'account-value-card__skeleton',
        wide ? 'account-value-card__skeleton--wide' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-hidden
    />
  );
}

function MetricRow({ label, value, secondary, loading, accent, inlineValues = false, subdued = false, matchSecondary = false, tooltip = null }) {
  const { colors } = useTheme();

  const labelStyle = tooltip
    ? {
        textDecoration: 'underline',
        textDecorationStyle: 'dotted',
        textDecorationColor: colors.textMuted,
        cursor: 'help',
      }
    : undefined;

  const labelNode = label ? (
    tooltip ? (
      <OverlayTrigger
        placement="top"
        delay={{ show: 200, hide: 100 }}
        overlay={
          <Tooltip id={`account-value-card-tip-${String(label).replace(/\s+/g, '-').toLowerCase()}`} className="account-value-card__tooltip">
            {tooltip}
          </Tooltip>
        }
      >
        <span className="account-value-card__metric-label" style={labelStyle}>
          {label}
        </span>
      </OverlayTrigger>
    ) : (
      <span className="account-value-card__metric-label">{label}</span>
    )
  ) : null;

  const secondaryNode =
    secondary != null && secondary !== '' ? (
      <span className="account-value-card__metric-secondary" style={{ color: accent || colors.textMuted }}>
        {secondary}
      </span>
    ) : null;

  return (
    <div
      className={[
        'account-value-card__metric-row',
        subdued ? 'account-value-card__metric-row--subdued' : '',
        matchSecondary ? 'account-value-card__metric-row--match-secondary' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {labelNode}
      <div
        className={[
          'account-value-card__metric-values',
          inlineValues ? 'account-value-card__metric-values--inline' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {loading ? (
          <MetricSkeleton />
        ) : (
          <>
            {/* Stacked rows read top-down (change % above amount); inline rows lead with the
                amount so the largest number is also the first one scanned. */}
            {!inlineValues && secondaryNode}
            <span
              className="account-value-card__metric-value"
              style={{ color: accent || (subdued ? colors.textMuted : colors.text) }}
            >
              {value ?? '—'}
            </span>
            {inlineValues && secondaryNode}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Reusable "Account Value" card used on Portfolio (AccountOverview) and Trade.
 *
 * Props are intentionally minimal and data-shape agnostic (numbers in, formatted strings out).
 */
export default function AccountValueCard({
  equity,
  dayPL,
  openPL,
  realizedPL,
  buyingPower,
  cash,
  marketValue,
  netWorth,
  netWorthLoading = false,
  netWorthBreakdown = [],
  showNetWorth = false,
  loading = false,
  isMobile = false,
  variant = 'default', // 'default' | 'compact-toggle'
  showPrivacyToggle = false,
  showBottomStats = true,
  violationsTodayCount,
  openPositionsCount,
  ordersFilledTodayCount,
  onViewAccountHealth,
  statsLoading = false,
  violationsLoading = false,
  defaultBottomExpanded = false,
  fillHeight = false,
}) {
  const { colors } = useTheme();
  const { user } = useContext(AuthContext) || {};
  const brokerLinked = isBrokerLinked(user);
  const [compact, setCompact] = useState(false);
  const [bottomExpanded, setBottomExpanded] = useState(defaultBottomExpanded);
  const [showConnect, setShowConnect] = useState(false);

  const eq = useMemo(() => (Number.isFinite(equity) ? equity : parseFloat(equity)), [equity]);
  const day = useMemo(() => (Number.isFinite(dayPL) ? dayPL : parseFloat(dayPL)), [dayPL]);
  const open = useMemo(() => (Number.isFinite(openPL) ? openPL : parseFloat(openPL)), [openPL]);
  const realized = useMemo(
    () => (Number.isFinite(realizedPL) ? realizedPL : parseFloat(realizedPL)),
    [realizedPL]
  );

  const dayPct = useMemo(() => (eq > 0 ? (day / eq) * 100 : null), [day, eq]);
  const openPct = useMemo(() => (eq > 0 ? (open / eq) * 100 : null), [open, eq]);
  const realizedPct = useMemo(() => (eq > 0 ? (realized / eq) * 100 : null), [realized, eq]);

  const netWorthTooltip = useMemo(() => {
    if (!Array.isArray(netWorthBreakdown) || netWorthBreakdown.length === 0) return null;
    const accountDefaults = user?.accountSettings || user?.accountDefaults;
    return (
      <div className="account-value-card__net-worth-tip">
        <div className="account-value-card__net-worth-tip-title">Live accounts</div>
        {netWorthBreakdown.map((entry) => (
          <div key={entry.accountId} className="account-value-card__net-worth-tip-row">
            <span className="account-value-card__net-worth-tip-account">
              <span className="account-value-card__net-worth-tip-broker">
                {formatBrokerDisplayName(entry.brokerName)}
              </span>
              <span className="account-value-card__net-worth-tip-id">
                {getAccountDisplayLabel(entry.accountId, accountDefaults)}
              </span>
            </span>
            <span className="account-value-card__net-worth-tip-value">
              {entry.isLoading ? '—' : fmtCurrency(entry.netWorth) ?? '—'}
            </span>
          </div>
        ))}
      </div>
    );
  }, [netWorthBreakdown, user?.accountSettings, user?.accountDefaults]);

  const sectionPad = isMobile ? '12px 14px' : '14px 18px';
  const cardBorder = colors.border || colors.borderLight;

  const isCompactVariant = variant === 'compact-toggle';
  const showDetails = !(isCompactVariant && compact);

  // A violation count of zero is not information worth a row next to the P&L figures.
  // Keep it alongside the other counts in the drawer until there is something to act on.
  const promoteViolations = showBottomStats && violationsTodayCount > 0;

  if (!brokerLinked) {
    return (
      <>
        <div
          className={[
            'account-value-card',
            'account-value-card--connect',
            isCompactVariant ? 'account-value-card--compact-toggle' : '',
            isMobile ? 'account-value-card--mobile' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{
            position: 'relative',
            overflow: 'hidden',
            minWidth: 0,
            width: '100%',
            borderColor: cardBorder,
          }}
        >
          <div
            className="account-value-card__connect"
            style={{
              padding: isCompactVariant ? '16px 14px' : sectionPad,
              borderColor: cardBorder,
            }}
          >
            <span className="account-value-card__title" style={{ color: colors.textMuted }}>
              Account Value
            </span>
            <p
              className="account-value-card__connect-message"
              style={{ color: colors.textMuted || colors.textSecondary }}
            >
              {isCompactVariant
                ? 'Connect a broker to see live account value and P&L.'
                : 'Connect your broker to see account value, P&L, and buying power.'}
            </p>
            <PrimaryButton size="sm" onClick={() => setShowConnect(true)}>
              <PlugFill size={15} />
              Connect broker
            </PrimaryButton>
          </div>
        </div>
        <ConnectBrokerModal show={showConnect} onHide={() => setShowConnect(false)} />
      </>
    );
  }

  return (
    <div
      className={[
        'account-value-card',
        isCompactVariant ? 'account-value-card--compact-toggle' : '',
        isMobile ? 'account-value-card--mobile' : '',
        fillHeight && !isMobile ? 'account-value-card--fill-height' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={{
        position: 'relative',
        overflow: 'hidden',
        minWidth: 0,
        width: '100%',
        borderColor: cardBorder,
      }}
    >
      <div
        className="account-value-card__section"
        style={{
          padding: sectionPad,
          borderBottom: showDetails ? `1px solid ${cardBorder}` : undefined,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: isCompactVariant ? undefined : isMobile ? 48 : 90,
          flexShrink: 0,
        }}
      >
        <div className="account-value-card__header-row">
          {showPrivacyToggle && isCompactVariant && (
            <button
              type="button"
              className="account-value-card__privacy-toggle"
              onClick={() => setCompact((p) => !p)}
              title={compact ? 'Show details' : 'Hide details'}
              style={{ color: colors.textMuted }}
            >
              {compact ? <EyeSlash size={16} /> : <Eye size={16} />}
            </button>
          )}

          <div className="account-value-card__header-text">
            <span className="account-value-card__title" style={{ color: colors.textMuted }}>
              Account Value
            </span>
            <span
              className={[
                'account-value-card__amount',
                isMobile && !isCompactVariant ? 'account-value-card__amount--mobile' : '',
                isCompactVariant ? 'account-value-card__amount--compact' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              style={{ color: colors.text }}
            >
              {loading ? <MetricSkeleton wide /> : fmtCurrency(eq) ?? '—'}
            </span>
          </div>
        </div>
      </div>

      {showDetails && (
        <div
          className={[
            'account-value-card__section',
            fillHeight && !isMobile ? 'account-value-card__section--body' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          style={{ padding: sectionPad }}
        >
          <div className="account-value-card__metrics">
            <MetricRow
              value={loading ? '—' : fmtCurrency(open)}
              secondary={loading ? null : fmtPercent(openPct)}
              label="Open P&L"
              accent={loading ? colors.textMuted : plAccent(open, colors)}
              inlineValues
            />
            <MetricRow
              value={loading ? '—' : fmtCurrency(day)}
              secondary={loading ? null : fmtPercent(dayPct)}
              label="Today's P&L"
              accent={loading ? colors.textMuted : plAccent(day, colors)}
              inlineValues
              matchSecondary
            />
            <MetricRow
              value={loading ? '—' : fmtCurrency(realized)}
              secondary={loading ? null : fmtPercent(realizedPct)}
              label="Realized P&L"
              accent={loading ? colors.textMuted : plAccent(realized, colors, { zeroIsWhite: true })}
              inlineValues
              matchSecondary
            />
            {promoteViolations && (
              <MetricRow
                value={violationsTodayCount}
                label="Violations Today"
                loading={violationsLoading}
                accent={colors.danger}
              />
            )}
          </div>

          {promoteViolations && onViewAccountHealth && (
            <button
              type="button"
              onClick={onViewAccountHealth}
              style={{
                marginTop: 8,
                background: 'none',
                border: 'none',
                padding: 0,
                fontSize: '0.78rem',
                fontWeight: 600,
                color: colors.danger,
                textDecoration: 'underline',
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              Review in Account Health
            </button>
          )}

          <div className="account-value-card__expand-block" style={{ marginTop: 10 }}>
            <div className="account-value-card__divider-row" style={{ margin: '4px 0 2px' }}>
              <button
                type="button"
                className="account-value-card__expand-button"
                onClick={() => setBottomExpanded((p) => !p)}
                title={bottomExpanded ? 'Collapse' : 'Expand'}
                aria-expanded={bottomExpanded}
                style={{ color: colors.textMuted }}
              >
                <span className="account-value-card__expand-label">
                  {bottomExpanded ? 'Less' : 'More'}
                </span>
                <div
                  className="account-value-card__divider-line"
                  style={{ background: cardBorder }}
                  aria-hidden
                />
                {bottomExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
            </div>

            {bottomExpanded && (
              <div className="account-value-card__metrics" style={{ marginTop: 8 }}>
                {showNetWorth && (
                  <MetricRow
                    value={loading || netWorthLoading ? '—' : fmtCurrency(netWorth)}
                    label="Net Worth"
                    loading={loading || netWorthLoading}
                    accent={loading || netWorthLoading ? colors.textMuted : colors.text}
                    tooltip={netWorthTooltip}
                  />
                )}
                <MetricRow
                  value={loading ? '—' : fmtCurrency(buyingPower)}
                  label="Buying Power"
                  accent={loading ? colors.textMuted : undefined}
                  subdued
                />
                <MetricRow
                  value={loading ? '—' : fmtCurrency(cash)}
                  label="Cash"
                  accent={loading ? colors.textMuted : undefined}
                  subdued
                />
                <MetricRow
                  value={loading ? '—' : fmtCurrency(marketValue)}
                  label="Market Value"
                  accent={loading ? colors.textMuted : undefined}
                  subdued
                />
                {showBottomStats && (
                  <>
                    <MetricRow
                      value={openPositionsCount}
                      label="Open Positions"
                      loading={statsLoading}
                    />
                    <MetricRow
                      value={ordersFilledTodayCount}
                      label="Filled Today"
                      loading={statsLoading}
                    />
                    {!promoteViolations && (
                      <MetricRow
                        value={violationsTodayCount}
                        label="Violations Today"
                        loading={violationsLoading}
                      />
                    )}
                    {onViewAccountHealth && (
                      <button
                        type="button"
                        onClick={onViewAccountHealth}
                        style={{
                          alignSelf: 'flex-start',
                          padding: 0,
                          marginTop: 4,
                          background: 'none',
                          border: 'none',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          color: colors.primary,
                          cursor: 'pointer',
                        }}
                      >
                        View account health
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

